import {
  AlertTriangle,
  ChevronLeft,
  ChevronRight,
  Plus,
  Search,
} from 'lucide-react';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AppButton from '../components/common/AppButton';
import AppLayout from '../components/layout/AppLayout';
import PoolIcon from '../components/layout/PoolIcon';
import PoolBillingBadge from '../components/pools/PoolBillingBadge';
import PoolStatusBadge from '../components/pools/PoolStatusBadge';
import PoolTypePill from '../components/pools/PoolTypePill';
import { usePoolCacheContext } from '../context/PoolCacheContext';
import { PoolStyleDisplay, type PoolStyle } from '../types/pool';

type StatusFilter = 'all' | 'active' | 'inactive';
type SortKey = 'name' | 'customer' | 'address';

const PAGE_SIZE = 10;

const PoolsPage = () => {
  const navigate = useNavigate();
  const {
    cacheData,
    loading,
    error: cacheError,
    searchPools,
    refreshCache,
  } = usePoolCacheContext();

  const pools = cacheData.poolsList;
  const error = cacheError ? 'Failed to load pools. Please try again.' : null;
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [sortKey, setSortKey] = useState<SortKey>('name');
  const [page, setPage] = useState(1);

  const counts = useMemo(() => {
    const active = pools.filter((p) => p.isActive !== false).length;
    return {
      all: pools.length,
      active,
      inactive: pools.length - active,
    };
  }, [pools]);

  const filteredPools = useMemo(() => {
    const searchTerm = search.trim();
    const list = searchTerm ? searchPools(searchTerm) : pools;

    return list
      .filter((pool) => {
        if (statusFilter === 'active' && pool.isActive === false) return false;
        if (statusFilter === 'inactive' && pool.isActive !== false)
          return false;
        return true;
      })
      .sort((a, b) => {
        switch (sortKey) {
          case 'customer':
            return a.customerName.localeCompare(b.customerName);
          case 'address':
            return a.poolAddress.localeCompare(b.poolAddress);
          case 'name':
          default:
            return a.poolName.localeCompare(b.poolName);
        }
      });
  }, [pools, search, statusFilter, sortKey, searchPools]);

  const totalPages = Math.max(1, Math.ceil(filteredPools.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const paginatedPools = filteredPools.slice(
    (currentPage - 1) * PAGE_SIZE,
    currentPage * PAGE_SIZE
  );

  const handleFilterChange = (filter: StatusFilter) => {
    setStatusFilter(filter);
    setPage(1);
  };

  const handleSearchChange = (value: string) => {
    setSearch(value);
    setPage(1);
  };

  const parseAddress = (address: string) => {
    // PoolCacheEntry.poolAddress is composed as `${street}, ${city}, ${state}`
    const parts = address.split(',').map((p) => p.trim());
    const street = parts[0] ?? address;
    const cityState = parts.slice(1).join(', ');
    return { street, cityState };
  };

  const hasNoPoolsAtAll = !loading && !cacheError && pools.length === 0;

  return (
    <AppLayout
      title="Pools"
      subtitle="Manage every pool your company services"
      actions={
        <AppButton leftIcon={<Plus />} onClick={() => navigate('/pools/new')}>
          Add Pool
        </AppButton>
      }
    >
      <div className="mt-6">
        {/* Toolbar */}
        <div className="flex items-center justify-between gap-4 flex-wrap mb-5">
          <div className="relative flex-1 max-w-[420px]">
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-[18px] h-[18px] text-grey10" />
            <input
              type="text"
              value={search}
              disabled={hasNoPoolsAtAll || !!error}
              onChange={(e) => handleSearchChange(e.target.value)}
              placeholder="Search by pool name, customer, or address…"
              className="w-full h-[46px] border border-grey rounded-xl bg-white pl-[42px] pr-3.5 text-sm text-dark placeholder:text-grey10 outline-none focus:border-primary focus:ring-4 focus:ring-primary/10 disabled:bg-grey4 disabled:cursor-not-allowed"
            />
          </div>

          <div className="flex gap-1.5 bg-regular rounded-xl p-[5px]">
            {(
              [
                { key: 'all', label: 'All', count: counts.all },
                { key: 'active', label: 'Active', count: counts.active },
                { key: 'inactive', label: 'Inactive', count: counts.inactive },
              ] as const
            ).map((chip) => (
              <button
                key={chip.key}
                type="button"
                onClick={() => handleFilterChange(chip.key)}
                className={`text-[13px] font-semibold px-4 py-2 rounded-lg transition-colors ${
                  statusFilter === chip.key
                    ? 'bg-white text-primary'
                    : 'text-light hover:text-dark'
                }`}
              >
                {chip.label} <span className="opacity-70">{chip.count}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Loading */}
        {loading && <PoolsTableSkeleton />}

        {/* Error state */}
        {!loading && error && (
          <div className="bg-white border border-grey3 rounded-[20px] px-8 py-[70px] text-center shadow-[0_6px_22px_rgba(0,29,47,0.05)]">
            <div className="w-[88px] h-[88px] mx-auto mb-5 rounded-full bg-dangerLight flex items-center justify-center text-danger">
              <AlertTriangle className="w-10 h-10" />
            </div>
            <h3 className="text-xl font-extrabold text-dark mb-2">
              Couldn&apos;t load pools
            </h3>
            <p className="text-sm text-light max-w-[380px] mx-auto mb-5">
              {error}
            </p>
            <AppButton className="mx-auto" onClick={refreshCache}>
              Retry
            </AppButton>
          </div>
        )}

        {/* Empty state */}
        {!error && hasNoPoolsAtAll && (
          <div className="bg-white border border-grey3 rounded-[20px] px-8 py-[70px] text-center shadow-[0_6px_22px_rgba(0,29,47,0.05)]">
            <div className="w-[88px] h-[88px] mx-auto mb-5 rounded-full bg-cloud flex items-center justify-center text-primary">
              <PoolIcon className="w-10 h-10" />
            </div>
            <h3 className="text-xl font-extrabold text-dark mb-2">
              No pools yet
            </h3>
            <p className="text-sm text-light max-w-[380px] mx-auto mb-5">
              Add your first pool to start scheduling routes, logging services
              and assigning technicians.
            </p>
            <AppButton
              leftIcon={<Plus />}
              className="mx-auto"
              onClick={() => navigate('/pools/new')}
            >
              Add your first pool
            </AppButton>
          </div>
        )}

        {/* Populated table */}
        {!loading && !cacheError && !hasNoPoolsAtAll && (
          <div className="bg-white border border-grey3 rounded-[20px] overflow-hidden shadow-[0_6px_22px_rgba(0,29,47,0.05)]">
            {filteredPools.length === 0 ? (
              <div className="px-8 py-16 text-center">
                <p className="text-dark font-semibold mb-1">No pools match</p>
                <p className="text-sm text-light">
                  Try a different search term or filter.
                </p>
              </div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="w-full border-collapse">
                    <thead>
                      <tr>
                        <SortableHeader
                          label="Name"
                          sortKey="name"
                          activeKey={sortKey}
                          onSort={setSortKey}
                        />
                        <SortableHeader
                          label="Customer"
                          sortKey="customer"
                          activeKey={sortKey}
                          onSort={setSortKey}
                        />
                        <SortableHeader
                          label="Address"
                          sortKey="address"
                          activeKey={sortKey}
                          onSort={setSortKey}
                        />
                        <th className="text-left text-[11px] font-bold uppercase tracking-wider text-grey10 px-5 py-4 bg-lighter border-b border-grey3 whitespace-nowrap">
                          Billing Model
                        </th>
                        <th className="text-left text-[11px] font-bold uppercase tracking-wider text-grey10 px-5 py-4 bg-lighter border-b border-grey3 whitespace-nowrap">
                          Type
                        </th>
                        <th className="text-left text-[11px] font-bold uppercase tracking-wider text-grey10 px-5 py-4 bg-lighter border-b border-grey3 whitespace-nowrap">
                          Status
                        </th>
                        <th className="bg-lighter border-b border-grey3" />
                      </tr>
                    </thead>
                    <tbody>
                      {paginatedPools.map((pool) => {
                        const { street, cityState } = parseAddress(
                          pool.poolAddress
                        );
                        return (
                          <tr
                            key={pool.id}
                            onClick={() => navigate(`/pools/${pool.id}`)}
                            className="border-b border-grey3 last:border-b-0 cursor-pointer hover:bg-cloud transition-colors group"
                          >
                            <td className="px-5 py-[15px] align-middle">
                              <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-[11px] bg-cloud text-primary flex items-center justify-center flex-shrink-0">
                                  <PoolIcon className="w-5 h-5" />
                                </div>
                                <div>
                                  <div className="text-sm font-bold text-dark">
                                    {pool.poolName}
                                  </div>
                                  <div className="text-xs text-grey10 mt-0.5">
                                    {PoolStyleDisplay[
                                      pool.poolStyle as PoolStyle
                                    ] ?? pool.poolStyle}
                                  </div>
                                </div>
                              </div>
                            </td>
                            <td className="px-5 py-[15px] align-middle">
                              <span className="text-sm font-semibold text-dark group-hover:text-primary">
                                {pool.customerName}
                              </span>
                            </td>
                            <td className="px-5 py-[15px] align-middle">
                              <div className="leading-tight">
                                <div className="text-sm font-medium text-dark">
                                  {street}
                                </div>
                                <div className="text-xs text-grey10">
                                  {cityState}
                                </div>
                              </div>
                            </td>
                            <td className="px-5 py-[15px] align-middle">
                              <PoolBillingBadge
                                billingModelType={pool.billingModelType}
                              />
                            </td>
                            <td className="px-5 py-[15px] align-middle">
                              <PoolTypePill poolType={pool.poolType} />
                            </td>
                            <td className="px-5 py-[15px] align-middle">
                              <PoolStatusBadge
                                isActive={pool.isActive !== false}
                              />
                            </td>
                            <td className="px-5 py-[15px] align-middle text-grey10 group-hover:text-primary">
                              <ChevronRight className="w-[18px] h-[18px]" />
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                <div className="flex items-center justify-between px-5 py-4 border-t border-grey3 bg-lighter">
                  <div className="text-sm text-light">
                    Showing{' '}
                    <strong className="text-dark">
                      {(currentPage - 1) * PAGE_SIZE + 1}–
                      {Math.min(currentPage * PAGE_SIZE, filteredPools.length)}
                    </strong>{' '}
                    of{' '}
                    <strong className="text-dark">
                      {filteredPools.length}
                    </strong>{' '}
                    pools
                  </div>
                  <div className="flex items-center gap-1.5">
                    <PagerButton
                      disabled={currentPage === 1}
                      onClick={() => setPage((p) => Math.max(1, p - 1))}
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </PagerButton>
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map(
                      (pageNum) => (
                        <PagerButton
                          key={pageNum}
                          active={pageNum === currentPage}
                          onClick={() => setPage(pageNum)}
                        >
                          {pageNum}
                        </PagerButton>
                      )
                    )}
                    <PagerButton
                      disabled={currentPage === totalPages}
                      onClick={() =>
                        setPage((p) => Math.min(totalPages, p + 1))
                      }
                    >
                      <ChevronRight className="w-4 h-4" />
                    </PagerButton>
                  </div>
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </AppLayout>
  );
};

interface SortableHeaderProps {
  label: string;
  sortKey: SortKey;
  activeKey: SortKey;
  onSort: (key: SortKey) => void;
}

const SortableHeader = ({
  label,
  sortKey,
  activeKey,
  onSort,
}: SortableHeaderProps) => {
  const isActive = activeKey === sortKey;
  return (
    <th
      onClick={() => onSort(sortKey)}
      className={`text-left text-[11px] font-bold uppercase tracking-wider px-5 py-4 bg-lighter border-b border-grey3 whitespace-nowrap cursor-pointer select-none ${
        isActive ? 'text-primary' : 'text-grey10'
      }`}
    >
      {label}
    </th>
  );
};

interface PagerButtonProps {
  children: React.ReactNode;
  active?: boolean;
  disabled?: boolean;
  onClick: () => void;
}

const PagerButton = ({
  children,
  active,
  disabled,
  onClick,
}: PagerButtonProps) => (
  <button
    type="button"
    disabled={disabled}
    onClick={onClick}
    className={`min-w-[34px] h-[34px] rounded-lg border text-[13px] font-semibold flex items-center justify-center transition-colors ${
      active
        ? 'bg-primary border-primary text-white'
        : 'bg-white border-grey text-dark hover:border-primary hover:text-primary disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:border-grey disabled:hover:text-dark'
    }`}
  >
    {children}
  </button>
);

const PoolsTableSkeleton = () => (
  <div className="bg-white border border-grey3 rounded-[20px] overflow-hidden shadow-[0_6px_22px_rgba(0,29,47,0.05)]">
    <div className="divide-y divide-grey3">
      {Array.from({ length: 6 }, (_, i) => (
        <div key={i} className="flex items-center gap-4 px-5 py-4">
          <div className="w-10 h-10 rounded-[11px] bg-gradient-to-r from-grey4 via-lighter to-grey4 bg-[length:400%_100%] animate-pulse" />
          <div className="flex-1 space-y-2">
            <div className="h-3.5 w-40 rounded bg-gradient-to-r from-grey4 via-lighter to-grey4 bg-[length:400%_100%] animate-pulse" />
            <div className="h-2.5 w-24 rounded bg-gradient-to-r from-grey4 via-lighter to-grey4 bg-[length:400%_100%] animate-pulse" />
          </div>
          <div className="h-3.5 w-28 rounded bg-gradient-to-r from-grey4 via-lighter to-grey4 bg-[length:400%_100%] animate-pulse" />
          <div className="h-3.5 w-24 rounded bg-gradient-to-r from-grey4 via-lighter to-grey4 bg-[length:400%_100%] animate-pulse" />
          <div className="h-6 w-20 rounded-full bg-gradient-to-r from-grey4 via-lighter to-grey4 bg-[length:400%_100%] animate-pulse" />
        </div>
      ))}
    </div>
  </div>
);

export default PoolsPage;
